import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeft, BarChart3, CalendarClock, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { countSegment, getBroadcast, getEmail, listSegments, saveBroadcast, saveEmail, scheduleBroadcast, type EmailDraft } from "@/lib/emailApi";
import { BRANDS, brandById } from "@/lib/brands";
import { addDays, formatInt, formatWhen, mountainDay } from "@/lib/format";
import type { Broadcast, BroadcastPreview, EmailDoc } from "@/lib/types";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label, Textarea } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { TestSend } from "./TestSend";
import { EmailPreviewPanel } from "./EmailPreviewPanel";
import { STATUS } from "./BroadcastsPage";
import { WhatUsesThis } from "../automations/WhatUsesThis";
import { AttendeeBadge, EmailLabelControl } from "./EmailLabel";

interface BrandRow {
  id: number;
  code: string;
  name: string;
  domain: string;
}

/** Write (or paste) the email, pick who gets it, check it light and dark, then schedule it. */
export function BroadcastEditorPage() {
  const { broadcastId } = useParams({ from: "/email/broadcasts/$broadcastId" });
  const search = useSearch({ from: "/email/broadcasts/$broadcastId" });
  const isNew = broadcastId === "new";
  const id = isNew ? 0 : Number(broadcastId);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const existing = useQuery({ queryKey: ["broadcast", id], queryFn: () => getBroadcast(id), enabled: id > 0 });
  const emailQ = useQuery({
    queryKey: ["email-doc", existing.data?.email_id],
    queryFn: () => getEmail(existing.data!.email_id!),
    enabled: !!existing.data?.email_id,
  });
  const segments = useQuery({ queryKey: ["segments"], queryFn: () => listSegments() });
  const brands = useQuery({ queryKey: ["brands"], queryFn: () => api<{ brands: (BrandRow & { marketing_from_email?: string })[] }>("/api/v1/brands"), staleTime: Infinity });

  const [brand, setBrand] = useState<number>(search.brand ?? 2);
  const [name, setName] = useState("");
  const [draft, setDraft] = useState<Omit<EmailDraft, "brand_id" | "name">>({ subject: "", preheader: "", from_name: "", from_email: "", reply_to: "", html: "", footer: "auto" });
  const [segmentId, setSegmentId] = useState<number | "">(search.segment ?? "");
  const [subjectB, setSubjectB] = useState("");
  const [emailId, setEmailId] = useState<number | null>(null);
  const [usesOpen, setUsesOpen] = useState(false);
  // Attendee email: a saved transactional email (class logistics) sent to a list instead of one written here.
  const [attendee, setAttendee] = useState(false);
  const [loaded, setLoaded] = useState(isNew);
  useEffect(() => {
    if (loaded || !existing.data || (existing.data.email_id && !emailQ.data)) return;
    const b = existing.data;
    setBrand(b.brand_id);
    setName(b.name);
    setSegmentId(b.segment_id ?? "");
    setSubjectB(b.subject_b ?? "");
    setAttendee(!!b.attendee_email);
    if (emailQ.data) {
      const e = emailQ.data;
      setEmailId(e.id);
      setDraft({ subject: e.version.subject, preheader: e.version.preheader ?? "", from_name: e.version.from_name, from_email: e.version.from_email, reply_to: e.version.reply_to ?? "", html: e.version.html ?? "", footer: (e.settings.footer as "auto" | "none") ?? "auto" });
    }
    setLoaded(true);
  }, [existing.data, emailQ.data, loaded]);

  const b = existing.data;
  const editable = !b || b.status === "draft";
  const transactional = useQuery({
    queryKey: ["emails-transactional", brand],
    queryFn: () => api<{ emails: { id: number; name: string; subject: string }[] }>(`/api/v1/emails?kind=transactional&brand_id=${brand}&limit=200`).then((r) => r.emails ?? []),
    enabled: attendee && editable,
  });
  const picked = useQuery({ queryKey: ["email-doc", emailId], queryFn: () => getEmail(emailId!), enabled: attendee && !!emailId });
  const seg = segments.data?.find((s) => s.id === segmentId);
  const counts = useQuery({
    queryKey: ["broadcast-audience", segmentId, brand],
    queryFn: () => countSegment({ segment_id: Number(segmentId), brand_id: brand }),
    enabled: !!segmentId,
    retry: false,
  });

  const save = useMutation({
    mutationFn: async () => {
      if (attendee) {
        // The transactional email is used as saved: this page doesn't change its wording.
        if (!emailId) throw new Error("Pick the transactional email first.");
        return saveBroadcast(id || null, { name: name.trim() || picked.data?.name || "Attendee email", email_id: emailId, segment_id: segmentId || undefined, brand_id: brand, subject_b: subjectB.trim() });
      }
      const e = await saveEmail(emailId, { ...draft, brand_id: brand, name: name.trim() || draft.subject });
      setEmailId(e.id);
      const r = await saveBroadcast(id || null, {
        name: name.trim() || draft.subject,
        email_id: e.id,
        segment_id: segmentId || undefined,
        brand_id: brand,
        subject_b: subjectB.trim(),
      });
      return r;
    },
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: ["broadcasts"] });
      void qc.invalidateQueries({ queryKey: ["broadcast", saved.id] });
      toast(attendee ? "Draft saved" : "Draft saved (a new version of the email)");
      if (isNew) navigate({ to: "/email/broadcasts/$broadcastId", params: { broadcastId: String(saved.id) }, replace: true });
    },
    onError: (e) => toast(e.message, "error"),
  });

  const loadError = existing.error ?? emailQ.error;
  if (!loaded && loadError) return <p className="text-sm text-[var(--color-destructive)]">Couldn't load this broadcast: {loadError.message}</p>;
  if (!loaded) return <Skeleton className="h-96" />;
  const brandRow = brands.data?.brands.find((x) => x.id === brand);
  const hasAudience = !!segmentId || (!!b?.definition && !b.segment_id);
  const ready = attendee ? !!emailId && hasAudience : !!draft.subject.trim() && !!draft.html.trim() && hasAudience;
  const canSave = attendee ? !!emailId : !!draft.html.trim() && !!draft.subject.trim();
  const shown = attendee
    ? { html: picked.data?.version.html ?? "", subject: picked.data?.version.subject ?? "", preheader: picked.data?.version.preheader ?? "" }
    : { html: draft.html, subject: draft.subject, preheader: draft.preheader ?? "" };

  return (
    <div className="mx-auto max-w-[1300px] space-y-3">
      <Link to="/email/broadcasts" className="inline-flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
        <ArrowLeft className="size-4" /> Broadcasts
      </Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">{isNew ? "New broadcast" : name || `Broadcast #${id}`}</h1>
        {b && <Badge variant={STATUS[b.status]?.variant ?? "muted"}>{STATUS[b.status]?.label ?? b.status}</Badge>}
        {b?.scheduled_at && b.status !== "draft" && <span className="text-sm text-[var(--color-muted-foreground)]">for {formatWhen(b.scheduled_at)}</span>}
        {attendee && <AttendeeBadge />}
        {emailQ.data && <EmailLabelControl email={emailQ.data} />}
        <span className="ml-auto flex gap-2">
          {emailId && (
            <Button size="sm" variant="outline" onClick={() => setUsesOpen(true)}>
              What uses this?
            </Button>
          )}
          {b?.report_id && (
            <Button size="sm" variant="outline" asChild>
              <Link to="/email/reports/$reportId" params={{ reportId: String(b.report_id) }}>
                <BarChart3 /> Report
              </Link>
            </Button>
          )}
          {editable && (
            <Button size="sm" variant="outline" disabled={save.isPending || !canSave} onClick={() => save.mutate()}>
              Save draft
            </Button>
          )}
        </span>
      </div>
      {usesOpen && emailId && <WhatUsesThis kind="email" idOrKey={emailId} onClose={() => setUsesOpen(false)} />}
      {!editable && <p className="text-sm text-[var(--color-muted-foreground)]">This broadcast is {STATUS[b!.status]?.label}; only drafts can be changed. Pause or cancel it from the Broadcasts list.</p>}

      <div className="grid gap-3 xl:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
        <div className="space-y-3">
          <Card>
            <CardHeader>
              <CardTitle>1. The email</CardTitle>
              {isNew && (
                <div role="radiogroup" aria-label="Kind of broadcast" className="flex gap-1">
                  <Button
                    size="sm"
                    role="radio"
                    aria-checked={!attendee}
                    variant={!attendee ? "default" : "outline"}
                    onClick={() => {
                      setAttendee(false);
                      setEmailId(null);
                    }}
                  >
                    Marketing email
                  </Button>
                  <Button
                    size="sm"
                    role="radio"
                    aria-checked={attendee}
                    variant={attendee ? "default" : "outline"}
                    onClick={() => {
                      setAttendee(true);
                      setEmailId(null);
                    }}
                  >
                    Attendee email
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {attendee ? (
                <AttendeePicker
                  editable={editable}
                  brand={brand}
                  setBrand={(v) => {
                    setBrand(v);
                    setEmailId(null);
                  }}
                  name={name}
                  setName={setName}
                  emails={transactional.data ?? []}
                  emailId={emailId}
                  setEmailId={setEmailId}
                  picked={picked.data}
                />
              ) : (
              <fieldset disabled={!editable} className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="bc-brand">University</Label>
                  <Select id="bc-brand" value={brand} onChange={(e) => setBrand(Number(e.target.value))}>
                    {BRANDS.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.short}: {x.name}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="bc-name">Name (only you see it)</Label>
                  <Input id="bc-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="MHU Boot Camp invite, Nov 2026" />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label htmlFor="bc-subject">Subject line</Label>
                  <Input id="bc-subject" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} placeholder="~Contact.FirstName~, last week for the early-bird price" />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label htmlFor="bc-subject-b">Second subject line for an A/B test (optional)</Label>
                  <Input id="bc-subject-b" value={subjectB} onChange={(e) => setSubjectB(e.target.value)} placeholder="Leave empty for one subject line" />
                  <p className="text-xs text-[var(--color-muted-foreground)]">
                    Half the people, picked at random, get this subject line and half get the one above. Everything else is the same and both go out together. The report compares them; nothing picks a winner for you.
                  </p>
                </div>
                <div className="col-span-2 space-y-1">
                  <Label htmlFor="bc-pre">Preview text (shown after the subject in the inbox)</Label>
                  <Input id="bc-pre" value={draft.preheader} onChange={(e) => setDraft({ ...draft, preheader: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="bc-fromname">From name</Label>
                  <Input id="bc-fromname" value={draft.from_name} onChange={(e) => setDraft({ ...draft, from_name: e.target.value })} placeholder="Frank and Dave" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="bc-from">From address</Label>
                  <Input id="bc-from" type="email" value={draft.from_email} onChange={(e) => setDraft({ ...draft, from_email: e.target.value })} placeholder={brandRow?.marketing_from_email ?? `FrankandDave@${brandById(brand)?.domain}`} />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label htmlFor="bc-html">HTML (paste the generator's output)</Label>
                  <Textarea
                    id="bc-html"
                    className="min-h-64 font-mono text-xs"
                    spellCheck={false}
                    value={draft.html}
                    onChange={(e) => setDraft({ ...draft, html: e.target.value })}
                    placeholder="<html>…</html>  Merge tokens like ~Contact.FirstName~ are filled in for each person."
                  />
                </div>
                <label className="col-span-2 flex items-start gap-2 text-sm">
                  <input type="checkbox" className="mt-1 accent-[var(--color-primary)]" checked={draft.footer !== "none"} onChange={(e) => setDraft({ ...draft, footer: e.target.checked ? "auto" : "none" })} />
                  <span>
                    Add the {brandById(brand)?.short} footer (postal address and unsubscribe link)
                    <span className="block text-xs text-[var(--color-muted-foreground)]">Leave it on unless the HTML already has its own unsubscribe link.</span>
                  </span>
                </label>
              </fieldset>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>2. Who gets it</CardTitle>
              <Link to="/email/segments/$segmentId" params={{ segmentId: "new" }} className="text-xs text-[var(--color-primary)] hover:underline">
                Build a new segment
              </Link>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Select value={segmentId} disabled={!editable} onChange={(e) => setSegmentId(e.target.value ? Number(e.target.value) : "")} aria-label="Segment">
                <option value="">Pick a segment…</option>
                {(segments.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.last_mailable != null ? ` (${formatInt(s.last_mailable)})` : ""}
                  </option>
                ))}
              </Select>
              {!segmentId && b?.definition && <p>Custom audience (rules set through the API). The review before scheduling counts who gets it.</p>}
              {attendee && (
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  Attendee email: unsubscribing and “no marketing” tags don't leave anyone out, so more people may get it than the count below. Review and schedule shows the exact number (at most 500).
                </p>
              )}
              {seg && counts.data && (
                <p>
                  <span className="text-lg font-semibold">{formatInt(counts.data.mailable)}</span> people would get it from {brandById(brand)?.short}
                  <span className="text-xs text-[var(--color-muted-foreground)]">
                    {" "}
                    ({formatInt(counts.data.matching)} match; the rest have no mailable address, unsubscribed, or carry a “no marketing” tag)
                  </span>
                </p>
              )}
              {seg && counts.error && <p className="text-xs text-[var(--color-destructive)]">Couldn't count the audience: {counts.error.message}</p>}
            </CardContent>
          </Card>

          <ScheduleCard broadcast={b} ready={ready && !!id && !save.isPending} saveFirst={() => save.mutateAsync()} />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>3. Check it: light and dark, phone and desktop</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <TestSend emailId={emailId} subjectB={subjectB} />
            <EmailPreviewPanel html={shown.html} brandId={brand} subject={shown.subject} preheader={shown.preheader} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ScheduleCard({ broadcast, ready, saveFirst }: { broadcast?: Broadcast; ready: boolean; saveFirst: () => Promise<unknown> }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [when, setWhen] = useState<"now" | "later">("later");
  const [day, setDay] = useState(addDays(mountainDay(), 1));
  const [time, setTime] = useState("09:00");
  const [preview, setPreview] = useState<BroadcastPreview | null>(null);
  const sendAt = when === "now" ? "now" : `${day} ${time}`;
  const review = useMutation({
    mutationFn: async () => {
      await saveFirst();
      return scheduleBroadcast(broadcast!.id, sendAt, false);
    },
    onSuccess: (r) => (r.preview ? setPreview(r.preview) : undefined),
    onError: (e) => toast(e.message, "error"),
  });
  const confirm = useMutation({
    mutationFn: () => scheduleBroadcast(broadcast!.id, sendAt, true),
    onSuccess: () => {
      setPreview(null);
      void qc.invalidateQueries({ queryKey: ["broadcast", broadcast!.id] });
      void qc.invalidateQueries({ queryKey: ["broadcasts"] });
      toast(`Scheduled for ${when === "now" ? "now" : `${day} ${time} MT`}`);
    },
    onError: (e) => toast(e.message, "error"),
  });
  if (broadcast && broadcast.status !== "draft") return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>4. Send</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div role="radiogroup" aria-label="When" className="flex flex-wrap items-center gap-2">
          <Button size="sm" role="radio" aria-checked={when === "later"} variant={when === "later" ? "default" : "outline"} onClick={() => setWhen("later")}>
            <CalendarClock /> At a set time
          </Button>
          <Button size="sm" role="radio" aria-checked={when === "now"} variant={when === "now" ? "default" : "outline"} onClick={() => setWhen("now")}>
            Right away
          </Button>
          {when === "later" && (
            <>
              <Input type="date" className="h-8 w-40" value={day} min={mountainDay()} onChange={(e) => setDay(e.target.value)} aria-label="Day" />
              <Input type="time" className="h-8 w-32" value={time} onChange={(e) => setTime(e.target.value)} aria-label="Time (Mountain)" />
              <span className="text-xs text-[var(--color-muted-foreground)]">Mountain Time</span>
            </>
          )}
        </div>
        <Button disabled={!ready || review.isPending} onClick={() => review.mutate()}>
          Review and schedule…
        </Button>
        {!broadcast && <p className="text-xs text-[var(--color-muted-foreground)]">Save the draft first.</p>}
        {broadcast && !ready && (
          <p className="text-xs text-[var(--color-muted-foreground)]">{broadcast.attendee_email ? "Needs the transactional email and a segment." : "Needs a subject, the HTML and a segment."}</p>
        )}
      </CardContent>
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="w-[min(600px,calc(100vw-2rem))]">
          <DialogTitle>Send “{preview?.subject}”?</DialogTitle>
          <DialogDescription>
            {preview?.send_at_mountain ? `At ${preview.send_at_mountain}` : "Right away"}, from {preview?.from}, to {preview?.audience}.
          </DialogDescription>
          {preview?.attendee_email && preview.attendee_counts && <AttendeeSummary preview={preview} />}
          {preview && !preview.attendee_email && (
            <div className="space-y-2 text-sm">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-semibold">{formatInt(preview.will_queue)}</span>
                <span className="text-[var(--color-muted-foreground)]">people will get it</span>
              </div>
              <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-0.5">
                <dt className="text-[var(--color-muted-foreground)]">Match the segment</dt>
                <dd className="num text-right">{formatInt(preview.counts.matching)}</dd>
                <dt className="text-[var(--color-muted-foreground)]">Left out: no mailable address</dt>
                <dd className="num text-right">{formatInt(preview.counts.no_mailable_address)}</dd>
                <dt className="text-[var(--color-muted-foreground)]">Left out: unsubscribed from this brand</dt>
                <dd className="num text-right">{formatInt(preview.counts.unsubscribed_brand)}</dd>
                <dt className="text-[var(--color-muted-foreground)]">Left out: “no marketing” tags</dt>
                <dd className="num text-right">{formatInt(preview.counts.skip_marketing_tags)}</dd>
                {preview.held_for_warmup > 0 && (
                  <>
                    <dt className="text-[var(--color-muted-foreground)]">Held back by the IP warm-up (stage {preview.warmup_stage})</dt>
                    <dd className="num text-right">{formatInt(preview.held_for_warmup)}</dd>
                  </>
                )}
              </dl>
              <p className="text-xs text-[var(--color-muted-foreground)]">
                {preview.estimated_days > 1 ? `The warm-up spreads it over ${preview.estimated_days} days. ` : ""}
                {sendTime(preview.estimated_hours_at_full_rate)}
                {preview.warmup_rule ? ` ${preview.warmup_rule}` : ""}
              </p>
              {preview.warnings.length > 0 && (
                <ul className="space-y-1 rounded-md bg-[var(--color-warning-bg)] px-3 py-2 text-xs text-[var(--color-warning-fg)]">
                  {preview.warnings.map((w) => (
                    <li key={w} className="flex gap-1.5">
                      <ShieldAlert className="mt-0.5 size-3.5 shrink-0" /> {w}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPreview(null)}>
              Not yet
            </Button>
            <Button disabled={confirm.isPending} onClick={() => confirm.mutate()}>
              {when === "now" ? "Send now" : `Schedule for ${day} ${time} MT`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function sendTime(hours: number): string {
  const min = Math.round(hours * 60);
  if (min < 2) return "It takes about a minute to send.";
  if (min < 90) return `It takes about ${min} minutes to send.`;
  return `It takes about ${hours.toFixed(1)} hours to send.`;
}

/** Attendee email, step 1: pick a saved transactional email of the university (its wording stays as saved). */
function AttendeePicker(p: {
  editable: boolean;
  brand: number;
  setBrand: (v: number) => void;
  name: string;
  setName: (v: string) => void;
  emails: { id: number; name: string; subject: string }[];
  emailId: number | null;
  setEmailId: (v: number | null) => void;
  picked?: EmailDoc;
}) {
  return (
    <fieldset disabled={!p.editable} className="grid grid-cols-2 gap-3 text-sm">
      <p className="col-span-2 rounded-md bg-[var(--color-warning-bg)] px-3 py-2 text-xs text-[var(--color-warning-fg)]">
        An attendee email sends a saved transactional email, such as a class's day-before logistics, to a list. It goes even to people who unsubscribed from marketing or carry a
        “no marketing” tag. Bounced, complained and blocked addresses never get it. No unsubscribe footer, at most 500 people, and anyone who already got this email is skipped.
        Never use it for marketing.
      </p>
      <div className="space-y-1">
        <Label htmlFor="bc-brand">University</Label>
        <Select id="bc-brand" value={p.brand} onChange={(e) => p.setBrand(Number(e.target.value))}>
          {BRANDS.map((x) => (
            <option key={x.id} value={x.id}>
              {x.short}: {x.name}
            </option>
          ))}
        </Select>
      </div>
      <div className="space-y-1">
        <Label htmlFor="bc-name">Name (only you see it)</Label>
        <Input id="bc-name" value={p.name} onChange={(e) => p.setName(e.target.value)} placeholder="Masterclass day-before email, Nov 2026" />
      </div>
      <div className="col-span-2 space-y-1">
        <Label htmlFor="bc-transactional">Transactional email</Label>
        <Select id="bc-transactional" value={p.emailId ?? ""} onChange={(e) => p.setEmailId(e.target.value ? Number(e.target.value) : null)}>
          <option value="">Pick a transactional email…</option>
          {p.picked && !p.emails.some((e) => e.id === p.picked!.id) && <option value={p.picked.id}>{p.picked.name}</option>}
          {p.emails.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name}
              {e.subject ? `: ${e.subject}` : ""}
            </option>
          ))}
        </Select>
        {p.editable && p.emails.length === 0 && <p className="text-xs text-[var(--color-muted-foreground)]">This university has no transactional emails yet.</p>}
      </div>
      {p.picked && (
        <dl className="col-span-2 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5">
          <dt className="text-[var(--color-muted-foreground)]">Subject</dt>
          <dd className="truncate">{p.picked.version.subject}</dd>
          <dt className="text-[var(--color-muted-foreground)]">From</dt>
          <dd className="truncate">{p.picked.version.from_name ? `${p.picked.version.from_name} <${p.picked.version.from_email}>` : p.picked.version.from_email}</dd>
        </dl>
      )}
    </fieldset>
  );
}

/** The review of an attendee email: what it is, who gets it, and who is left out or included anyway. */
function AttendeeSummary({ preview }: { preview: BroadcastPreview }) {
  const c = preview.attendee_counts!;
  const warnings = preview.warnings.filter((w) => w !== preview.attendee_note);
  return (
    <div className="space-y-2 text-sm" data-testid="attendee-summary">
      <p role="note" className="rounded-md bg-[var(--color-warning-bg)] px-3 py-2 text-[var(--color-warning-fg)]">
        {preview.attendee_note}
      </p>
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-semibold">{formatInt(preview.will_queue)}</span>
        <span className="text-[var(--color-muted-foreground)]">people will get it</span>
      </div>
      <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-0.5">
        <dt className="text-[var(--color-muted-foreground)]">Match the audience</dt>
        <dd className="num text-right">{formatInt(c.matching)}</dd>
        <dt className="text-[var(--color-muted-foreground)]">Left out: no address that can get mail (bounced, complained, blocked)</dt>
        <dd className="num text-right">{formatInt(c.no_mailable_address)}</dd>
        <dt className="text-[var(--color-muted-foreground)]">Skipped: already got this email</dt>
        <dd className="num text-right">{formatInt(c.already_sent)}</dd>
        <dt className="text-[var(--color-muted-foreground)]">Included even though unsubscribed from marketing</dt>
        <dd className="num text-right">{formatInt(c.unsubscribed_included)}</dd>
        <dt className="text-[var(--color-muted-foreground)]">Included even though tagged “no marketing”</dt>
        <dd className="num text-right">{formatInt(c.skip_marketing_included)}</dd>
      </dl>
      <p className="text-xs text-[var(--color-muted-foreground)]">{sendTime(preview.estimated_hours_at_full_rate)}</p>
      {warnings.length > 0 && (
        <ul className="space-y-1 rounded-md bg-[var(--color-warning-bg)] px-3 py-2 text-xs text-[var(--color-warning-fg)]">
          {warnings.map((w) => (
            <li key={w} className="flex gap-1.5">
              <ShieldAlert className="mt-0.5 size-3.5 shrink-0" /> {w}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
